import { auth, httpsCallable, functionsEu } from '../firebase';
import type { TonyteActivity } from '../types';

type PlanMembershipData = Partial<TonyteActivity> & {
  members?: unknown;
  approvedRequests?: unknown;
  adminId?: unknown;
};

const stringIds = (value: unknown): string[] => (
  Array.isArray(value)
    ? value.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    : []
);

/** `plans/{planId}.participantIds` is the canonical Plan membership source. */
export function getPlanMemberIds(plan: PlanMembershipData | null | undefined): string[] {
  if (!plan) return [];
  const hasCanonicalIds = Array.isArray(plan.participantIds);
  const candidateIds = hasCanonicalIds
    ? stringIds(plan.participantIds)
    : [
        ...stringIds(plan.members),
        ...stringIds(plan.approvedRequests),
        ...(Array.isArray(plan.attendees)
          ? plan.attendees.flatMap(attendee => {
              if (!attendee || typeof attendee !== 'object') return [];
              const data = attendee as Record<string, unknown>;
              const id = typeof data.id === 'string' ? data.id : data.userId;
              return typeof id === 'string' && id.trim() ? [id] : [];
            })
          : []),
      ];
  const creatorIds = [plan.createdBy, plan.hostId, plan.adminId, ...stringIds(plan.adminIds)]
    .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  const removed = new Set(stringIds(plan.removedUserIds));
  return [...new Set([...creatorIds, ...candidateIds])].filter(id => !removed.has(id));
}

export const getPlanMemberCount = (plan: PlanMembershipData | null | undefined) => getPlanMemberIds(plan).length;

export const isPlanMember = (plan: PlanMembershipData | null | undefined, userId: string | null | undefined) =>
  Boolean(userId && getPlanMemberIds(plan).includes(userId));

/**
 * Server-authoritative removal of a Plan member by its creator/admin — see
 * functions/index.js's removePlanMemberHandler for the full transaction
 * (plans + planChats + join-request cleanup, all-or-nothing). Never a client
 * writeBatch: this removes SOMEONE ELSE, which Firestore rules deliberately
 * do not trust a client to self-certify.
 */
export async function removePlanMember(planId: string, targetUserId: string): Promise<void> {
  // Force a fresh ID token before calling — a signed-in session that's been
  // open for a while (or backgrounded, where the SDK's background refresh
  // timer doesn't run) can hand httpsCallable() a cached, already-expired
  // token; the callable framework then verifies nothing and the Cloud
  // Function correctly sees no request.auth, surfacing as a bare
  // 'unauthenticated' error even though the user is genuinely signed in.
  // Other admin-only Eu callables in this app (see ProfileScreen.tsx's
  // face-verification review/getIdToken(true) calls, useAuth.ts,
  // useProfile.ts) already guard against this the same way.
  await auth.currentUser?.getIdToken(true);
  const fn = httpsCallable<{ planId: string; targetUserId: string }, { success: boolean }>(
    functionsEu,
    'removePlanMemberEu',
  );
  await fn({ planId, targetUserId });
}
